// UX RULES — Explicit error states for all Brain/Cerveau failures
import { AlertTriangle, Clock, RefreshCw, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type BrainErrorType = "timeout" | "rate_limit" | "unavailable" | "generation_failed";

interface BrainErrorStateProps {
  errorType: BrainErrorType;
  onRetry?: () => void;
  waitMinutes?: number;
  /** If true, renders inline (no card border) for chat contexts */
  inline?: boolean;
  className?: string;
}

const ERROR_CONFIG: Record<BrainErrorType, {
  icon: typeof AlertTriangle;
  title: string;
  message: string | ((waitMinutes?: number) => string);
  showRetry: boolean;
  retryLabel?: string;
  retryDelay?: number; // ms delay before retry is enabled
}> = {
  timeout: {
    icon: Clock,
    title: "Délai dépassé",
    message: "Le Cerveau prend plus de temps que prévu. Réessaie dans quelques instants.",
    showRetry: true,
    retryLabel: "Réessayer",
  },
  rate_limit: {
    icon: AlertTriangle,
    title: "Limite atteinte",
    message: (waitMinutes?: number) =>
      waitMinutes
        ? `Tu as atteint la limite de questions pour cette heure. Reviens dans ${waitMinutes} minute${waitMinutes > 1 ? "s" : ""}.`
        : "Tu as atteint la limite de questions pour cette heure. Reviens dans quelques minutes.",
    showRetry: false,
  },
  unavailable: {
    icon: WifiOff,
    title: "Cerveau indisponible",
    message: "Le Cerveau est temporairement indisponible. Nos équipes sont informées.",
    showRetry: true,
    retryLabel: "Réessayer",
    retryDelay: 5000,
  },
  generation_failed: {
    icon: AlertTriangle,
    title: "Échec de génération",
    message: "La génération de ton programme a échoué. Ton profil est intact — réessaie.",
    showRetry: true,
    retryLabel: "Réessayer la génération",
  },
};

export function BrainErrorState({
  errorType,
  onRetry,
  waitMinutes,
  inline = false,
  className,
}: BrainErrorStateProps) {
  const config = ERROR_CONFIG[errorType];
  const Icon = config.icon;
  const message = typeof config.message === "function"
    ? config.message(waitMinutes)
    : config.message;

  const handleRetry = () => {
    if (config.retryDelay) {
      setTimeout(() => onRetry?.(), config.retryDelay);
    } else {
      onRetry?.();
    }
  };

  if (inline) {
    return (
      <div className={cn("flex flex-col gap-2 py-2", className)}>
        <div className="flex items-start gap-2">
          <Icon className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{message}</p>
        </div>
        {config.showRetry && onRetry && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleRetry}
            className="self-start min-h-[44px] text-xs border-destructive/30 text-destructive hover:bg-destructive/10"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            {config.retryLabel}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={cn(
      "rounded-xl border border-destructive/30 bg-destructive/5 p-5 space-y-3",
      className,
    )}>
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-destructive">{config.title}</p>
          <p className="text-sm text-muted-foreground">{message}</p>
        </div>
      </div>
      {config.showRetry && onRetry && (
        <Button
          variant="outline"
          onClick={handleRetry}
          className="min-h-[44px] border-destructive/30 text-destructive hover:bg-destructive/10"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          {config.retryLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * Maps HTTP status codes and error conditions to BrainErrorType.
 * Use this in hooks to determine which error state to show.
 */
export function classifyBrainError(
  status?: number,
  error?: { message?: string; code?: string } | null,
  isTimeout?: boolean,
): BrainErrorType {
  if (isTimeout) return "timeout";
  if (status === 429) return "rate_limit";
  if (status === 503 || status === 502) return "unavailable";
  if (status === 504) return "timeout";
  if (status === 402) return "rate_limit"; // credits exhausted
  return "unavailable"; // default fallback
}
